/**
 * @file CoachingIndividuelCompetition.tsx
 * @summary Article de blog — CoachingIndividuelCompetition
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, AdaptationChistera, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/coaching-individuel-competition-hyrox-athx-crossfit";
const TITLE = "Coaching individuel HYROX, ATHX, CrossFit : programmation 12-24 semaines";
const DESC = "Préparer une compétition HYROX, ATHX ou CrossFit avec un coaching individuel structuré : périodisation, technique, mental, gestion de la charge. Approche CrossFit Chistera.";

const author = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };
const reviewer = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };

const faq = [
  { q: "Combien de temps faut-il pour préparer une compétition ?", a: "Pour un premier HYROX ou un Open CrossFit en finissant solidement : 12 semaines minimum. Pour viser un classement ou une qualification (ATHX, finales régionales) : 16 à 24 semaines de préparation structurée." },
  { q: "Le coaching individuel remplace-t-il les WOD de groupe ?", a: "Non, il les complète. Les séances de groupe restent la base (intensité, ambiance, partage). Le coaching ajoute 2 à 4 séances ciblées par semaine sur vos points faibles, avec ajustements hebdo." },
  { q: "À qui s'adresse vraiment ce format ?", a: "Athlètes ayant un objectif daté (HYROX, ATHX, Open, qualif), ou qui plafonnent depuis 6+ mois et veulent débloquer leur progression. Niveau intermédiaire à avancé recommandé." },
  { q: "Combien ça coûte ?", a: "Tarifs sur devis selon la durée du cycle, le nombre de séances individuelles incluses et le suivi (programmation hebdo, vidéo, échanges). On en discute lors d'un échange découverte gratuit." },
  { q: "Et si je me blesse en cours de préparation ?", a: "On rebascule sur de la réathlétisation ciblée, sans casser la dynamique. C'est tout l'intérêt du suivi individuel — la programmation s'adapte à votre réalité, pas l'inverse." },
];

export default function CoachingIndividuelCompetition() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Coaching individuel HYROX, ATHX, CrossFit"
      breadcrumbLabel="Coaching individuel compétition"
      category="Coaching · Compétition"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-01" readingMinutes={9}
      ctaVariant="competition"
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-01", author, reviewer, faq: faq.map(f => ({ q: f.q, a: typeof f.a === "string" ? f.a : "" })) })}
    >
      <EnBref>
        <p>Une vraie préparation compétition, ce n'est pas "faire plus de WOD". C'est <strong>périodiser</strong> sur 12 à 24 semaines, travailler ses <strong>points faibles</strong> de façon ciblée, et arriver le jour J <strong>frais et confiant</strong>. Le coaching individuel chez Chistera intègre programmation hebdo, séances dédiées, suivi technique et gestion mentale.</p>
      </EnBref>

      <h2>Pourquoi un coaching individuel pour une compétition ?</h2>
      <p>Les WOD de groupe développent une excellente base : intensité, capacité de travail, technique générale. Mais préparer un objectif précis demande autre chose : <strong>une programmation construite autour de vous</strong>, de votre calendrier, de vos forces et de vos faiblesses.</p>
      <p>Concrètement, un athlète qui prépare un HYROX n'a pas les mêmes besoins qu'un autre qui vise une qualification CrossFit régionale ou un ATHX. Le format de l'épreuve, les filières énergétiques sollicitées, les mouvements clés — tout change.</p>

      <h2>Les 3 grandes phases d'une préparation</h2>
      <h3>Phase 1 — Construction (semaines 1 à 8)</h3>
      <p>On développe les <strong>capacités générales</strong> : volume aérobie, force absolue, qualité technique. C'est la phase la moins glamour mais la plus rentable. On accumule du travail, on corrige les patterns, on installe les fondations.</p>

      <h3>Phase 2 — Spécifique (semaines 8 à 16)</h3>
      <p>On rapproche l'entraînement du format de l'épreuve : <strong>distances HYROX réelles</strong>, <strong>intervalles type Open</strong>, <strong>chipper longs pour ATHX</strong>. La filière énergétique ciblée prend le dessus. Les points faibles identifiés en phase 1 sont travaillés isolément.</p>

      <h3>Phase 3 — Affûtage (semaines 16 à 24)</h3>
      <p>On baisse le volume, on garde l'intensité. L'objectif : arriver <strong>frais, sans accumuler de fatigue résiduelle</strong>. Travail de stratégie de course, simulation d'épreuve, visualisation. Cette phase est souvent bâclée ; c'est pourtant elle qui transforme une bonne prépa en performance.</p>

      <h2>Ce qu'on travaille concrètement chez Chistera</h2>
      <ul>
        <li><strong>Programmation individualisée</strong> remise chaque dimanche pour la semaine à venir.</li>
        <li><strong>1 à 4 séances individuelles par semaine</strong> selon le format, avec coach dédié.</li>
        <li><strong>Analyse vidéo</strong> des mouvements techniques (snatch, muscle-up, walking lunges chargés…).</li>
        <li><strong>Suivi de la charge</strong> (RPE, heures de sommeil, fatigue) pour éviter le surentraînement.</li>
        <li><strong>Préparation mentale</strong> : gestion du stress de compétition, pacing, plan B en cas de pépin.</li>
        <li><strong>Stratégie de course</strong> détaillée 2 semaines avant l'épreuve.</li>
      </ul>

      <AdaptationChistera title="Comment ça se passe chez Chistera">
        <ol>
          <li><strong>Échange découverte gratuit</strong> (45 min) — objectif, calendrier, contraintes pro/perso.</li>
          <li><strong>Bilan initial</strong> — tests physiques, vidéo technique, identification des points faibles.</li>
          <li><strong>Cycle proposé</strong> avec dates clés, volume hebdo prévu, séances incluses.</li>
          <li><strong>Suivi hebdo</strong> par votre coach référent, ajustements en temps réel.</li>
          <li><strong>Débrief post-compétition</strong> et plan pour la suite.</li>
        </ol>
      </AdaptationChistera>

      <h2>Pour quel profil ?</h2>
      <p>Ce format s'adresse aux athlètes qui ont <strong>un objectif daté</strong> dans les 6 prochains mois (HYROX Lyon, ATHX, Open CrossFit, qualif régionale) ou qui veulent <strong>débloquer un plateau</strong> persistant. Niveau intermédiaire à avancé recommandé — pour les débutants, mieux vaut d'abord 6 à 12 mois de WOD réguliers.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Reprendre le sport après une blessure (la phase de réathlétisation)", to: "/blog/reprendre-le-sport-apres-blessure" },
        { label: "Compétitions CrossFit Open à Lyon", to: "/blog/competitions-crossfit-open-lyon" },
        { label: "Notre programme Hybrid Training", to: "/cours/hybrid-training" },
      ]} />

      <SourcesSection items={[
        { label: "Bompa & Buzzichelli — Periodization: Theory and Methodology of Training (6e éd., 2018)", href: "https://www.humankinetics.com/products/periodization-6th-edition" },
        { label: "Issurin VB — New horizons for the methodology and physiology of training periodization. Sports Med, 2010", href: "https://pubmed.ncbi.nlm.nih.gov/20199121/" },
      ]} />
    </ArticleLayout>
  );
}
